import { useUpdateUser, useUser } from "@/api/auth";
import { useAuth } from "@/context/auth/AuthContext";
import { toast } from "@/hooks/use-toast";
import { zodResolver } from "@hookform/resolvers/zod";
import { ComponentProps, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { Button } from "../ui/button";
import { CustomSheetFooter } from "../ui/custom-sheet";
import { Form, FormControl, FormField, FormItem, FormLabel } from "../ui/form";
import { FormCol } from "../ui/form-col";
import { Input } from "../ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../ui/sheet";

const schema = z.object({
  name: z.string().min(1),
  email: z.string().email(),
  phone: z.string(),
});

export const EditProfileModale = ({
  onOpenChange,
  ...sheetProps
}: ComponentProps<typeof Sheet>) => {
  const auth = useAuth();
  const { t } = useTranslation();

  const { data } = useUser(auth.token);

  const currentUser = useMemo(() => data?.at(0) ?? undefined, [data]);
  const userUpdater = useUpdateUser(auth.user?.id ?? "");

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: {
      ...currentUser,
    },
  });

  const { reset } = form;

  function onSubmit(values: z.infer<typeof schema>) {
    if (!auth.user) return;

    userUpdater.mutate(
      { dto: values, userId: auth.user.id },
      {
        onError: () => {
          toast({
            title: "errorUpdateProfileTitle",
            description: "errorUpdateProfileDescription",
            variant: "error",
          });
        },
        onSuccess: () => {
          toast({
            title: "successUpdateProfileTitle",
            description: "successUpdateProfileDescription",
            variant: "success",
          });
        },
      },
    );
  }

  function onCancel() {
    form.reset();
    if (onOpenChange) onOpenChange(false);
  }

  useEffect(() => reset({ ...currentUser }), [reset, currentUser]);

  return (
    <Sheet {...sheetProps} onOpenChange={onOpenChange}>
      <SheetContent className="w-[500px] px-0">
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className={`flex flex-col h-full`}
          >
            <SheetHeader className="p-8 py-4 border-b">
              <SheetTitle>{t("userFormTitle")}</SheetTitle>
            </SheetHeader>
            <div className="h-full p-8 gap-8">
              <FormCol>
                <FormField
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("settingsMyName")}</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder={t("settingsMyNamePlaceholder")}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("settingsMyEmail")}</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder={t("settingsMyEmailPlaceholder")}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("settingsMyPhone")}</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder={t("settingsMyPhonePlaceholder")}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </FormCol>
            </div>

            <CustomSheetFooter>
              <Button
                type="button"
                variant={"link"}
                onClick={onCancel}
                className="w-full"
              >
                {t("cancel")}
              </Button>
              <Button type="submit" variant={"default"}>
                {t("userProfileUpdateBtn")}
              </Button>
            </CustomSheetFooter>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  );
};
